import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BellRing, ClipboardList, Loader2, Maximize2, Minimize2, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdvisorCarRow } from "@/components/advisor/AdvisorCarRow";
import { AdvisorDetailPanel } from "@/components/advisor/AdvisorDetailPanel";
import { AdvisorCustomerView } from "@/components/advisor/AdvisorCustomerView";
import { DeskAlertBanner } from "@/components/advisor/DeskAlertBanner";
import { DeskAlertsBell } from "@/components/advisor/DeskAlertsBell";
import { HandoverPinPad } from "@/components/advisor/HandoverPinPad";
import { LunchCoverButton } from "@/components/advisor/LunchCoverButton";
import { useDeskAlerts } from "@/components/advisor/useDeskAlerts";
import { useHandoverLock } from "@/components/advisor/useHandoverLock";
import { isAwaitingApproval, isToday, outstanding } from "@/components/advisor/advisorUtils";

// ADVISOR DESK — the service advisor's counterpart to the Workshop Wall.
// Built for a large iPad on the desk: big tap targets, list on the left,
// everything about the selected car on the right, and the actions the advisor
// actually needs (call, text, chase, mark ready) one tap away.

const TABS = [
  { key: "today", label: "Today", icon: ClipboardList },
  { key: "approvals", label: "Approvals", icon: BellRing },
  { key: "customer", label: "Show customer", icon: Users },
];

const FLASH_MS = 20_000;
const BANNER_MS = 15_000;

export default function AdvisorDeskPage() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => localStorage.getItem("advisor_tab") || "today");
  const [selectedId, setSelectedId] = useState(null);
  const [isFull, setIsFull] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [flash, setFlash] = useState({});
  const [banners, setBanners] = useState([]);
  const [pinMode, setPinMode] = useState(null);
  const rootRef = useRef(null);
  const tabRef = useRef(tab);
  tabRef.current = tab;

  const handover = useHandoverLock();
  const lockedJob = handover.lockedJobId ? jobs.find((j) => j.id === handover.lockedJobId) : null;

  const load = async () => {
    try {
      const r = await api.get("/jobs");
      setJobs(r.data || []);
      setLoadError(false);
    } catch {
      setLoadError(true);
    } finally { setLoading(false); }
  };

  const onEvents = (evs) => {
    load();
    const ids = evs.map((e) => e.job_id).filter(Boolean);
    if (ids.length) {
      setFlash((f) => ({ ...f, ...Object.fromEntries(ids.map((id) => [id, Date.now()])) }));
      setTimeout(() => setFlash((f) => {
        const next = { ...f };
        ids.forEach((id) => { if (Date.now() - (next[id] || 0) >= FLASH_MS - 50) delete next[id]; });
        return next;
      }), FLASH_MS);
    }
    if (tabRef.current === "customer") return false;
    setBanners((b) => [...evs, ...b].slice(0, 5));
    evs.forEach((e) => setTimeout(() => dismissBanner(e.id), BANNER_MS));
    return true;
  };
  const alerts = useDeskAlerts(onEvents);
  const dismissBanner = (id) => setBanners((b) => b.filter((x) => x.id !== id));

  useEffect(() => {
    load();
    const poll = setInterval(load, 20_000);
    const onFs = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => { clearInterval(poll); document.removeEventListener("fullscreenchange", onFs); };
  }, []);

  // A locked job that has since left the board (collected + archived) frees the iPad.
  useEffect(() => {
    if (!loading && !loadError && handover.lockedJobId && !lockedJob) handover.release();
  }, [loading, loadError, handover.lockedJobId, lockedJob]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickTab = (k) => { setTab(k); localStorage.setItem("advisor_tab", k); };

  const lists = useMemo(() => {
    const live = (jobs || []).filter((j) => !["rejected"].includes(j.status));
    const today = live
      .filter((j) => j.status !== "completed" && (isToday(j) || j.is_carried_over))
      .sort((a, b) => (a.promised_ready_time || "99:99").localeCompare(b.promised_ready_time || "99:99"));
    const approvals = live
      .filter(isAwaitingApproval)
      .sort((a, b) => new Date(a.sent_at || a.updated_at || 0) - new Date(b.sent_at || b.updated_at || 0));
    return { today, approvals };
  }, [jobs]);

  const rows = tab === "approvals" ? lists.approvals : lists.today;
  const selected = jobs.find((j) => j.id === selectedId) || null;
  const outstandingTotal = lists.approvals.reduce((n, j) => n + outstanding(j), 0);

  const selectJob = (job) => setSelectedId(job.id);
  const showCustomer = (job) => { setSelectedId(job.id); pickTab("customer"); };
  const openAlert = (ev) => {
    dismissBanner(ev.id);
    if (!ev.job_id) return;
    setSelectedId(ev.job_id);
    if (tab === "customer") pickTab("today");
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else rootRef.current?.requestFullscreen?.();
  };

  const submitPin = (pin) => {
    if (pinMode === "set") {
      handover.lock(selected.id, pin);
      setPinMode(null);
    } else if (handover.tryUnlock(pin)) {
      setPinMode(null);
      pickTab("today");
    }
  };

  const pinPad = pinMode && (
    <HandoverPinPad
      mode={pinMode}
      onSubmit={submitPin}
      onCancel={() => setPinMode(null)}
      error={pinMode === "unlock" ? handover.error : ""}
      cooldownUntil={pinMode === "unlock" ? handover.cooldownUntil : null}
      hasSavedPin={handover.hasSavedPin}
      onUseSaved={() => { handover.lock(selected.id, null); setPinMode(null); }}
    />
  );

  if (handover.lockedJobId) {
    return (
      <div ref={rootRef} className="h-screen bg-zinc-100" data-testid="advisor-handover-locked">
        {lockedJob ? (
          <AdvisorCustomerView job={lockedJob} locked onUnlockRequest={() => setPinMode("unlock")} />
        ) : (
          <div className="h-full flex items-center justify-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        )}
        {pinPad}
      </div>
    );
  }

  if (tab === "customer" && selected) {
    return (
      <div ref={rootRef} className="h-screen bg-zinc-100">
        <AdvisorCustomerView job={selected} onBack={() => pickTab("today")} onLock={() => setPinMode("set")} />
        {pinPad}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="h-screen bg-slate-950 text-slate-100 flex flex-col select-none" data-testid="advisor-desk">
      <header className="px-4 sm:px-6 py-3 border-b border-slate-800 bg-slate-900/70 flex items-center gap-4 flex-wrap">
        <Link to="/dashboard" className="text-slate-500 hover:text-slate-300 shrink-0" data-testid="advisor-back-link" title="Back to dashboard">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <div className="min-w-0">
          <div className="text-[10px] font-black uppercase tracking-[0.25em] text-emerald-400">Advisor Desk</div>
          <div className="font-bold text-lg truncate" data-testid="advisor-workshop-name">{user?.workshop_name || "Workshop"}</div>
        </div>

        <nav className="flex-1 flex justify-center order-3 w-full lg:order-none lg:w-auto" data-testid="advisor-tabs">
          <div className="inline-flex gap-1 p-1 rounded-2xl bg-slate-800/80">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key} type="button" onClick={() => pickTab(key)}
                disabled={key === "customer" && !selected}
                className={`min-h-[52px] px-4 sm:px-6 rounded-xl font-bold text-base inline-flex items-center gap-2.5 transition-colors duration-150 disabled:opacity-40 ${
                  tab === key ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-slate-700"
                }`}
                data-testid={`advisor-tab-${key}`}
              >
                <Icon className="w-5 h-5" /> {label}
                {key === "approvals" && lists.approvals.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-slate-950/40 text-sm">{lists.approvals.length}</span>
                )}
              </button>
            ))}
          </div>
        </nav>

        <LunchCoverButton />
        <DeskAlertsBell
          recent={alerts.recent}
          unread={alerts.unread}
          onSeen={alerts.markSeen}
          muted={alerts.muted}
          onToggleMute={() => alerts.setMuted(!alerts.muted)}
          onOpen={openAlert}
        />
        <button type="button" onClick={toggleFullscreen} className="hidden sm:inline-flex p-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 shrink-0" data-testid="advisor-fullscreen-btn" title="Fullscreen">
          {isFull ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
        </button>
      </header>

      <DeskAlertBanner alerts={banners} onOpen={openAlert} onDismiss={dismissBanner} />

      <main className="flex-1 min-h-0 grid lg:grid-cols-[minmax(360px,42%)_1fr]">
        <section className="min-h-0 flex flex-col border-r border-slate-800">
          {loadError && (
            <div className="px-4 py-2.5 bg-red-500/15 border-b border-red-500/40 text-red-200 text-sm font-semibold" data-testid="advisor-load-error">
              Couldn&apos;t refresh the board — retrying every 20 seconds.
            </div>
          )}
          <div className="px-4 py-2.5 border-b border-slate-800 flex items-center justify-between text-sm">
            <span className="font-bold text-slate-300" data-testid="advisor-list-count">
              {rows.length} car{rows.length === 1 ? "" : "s"}
            </span>
            {tab === "approvals" && outstandingTotal > 0 && (
              <span className="font-bold text-amber-300" data-testid="advisor-outstanding-total">
                £{outstandingTotal.toLocaleString("en-GB", { maximumFractionDigits: 0 })} awaiting a yes
              </span>
            )}
          </div>
          <div className="flex-1 overflow-auto p-3 space-y-2.5" data-testid="advisor-car-list">
            {loading && (
              <div className="flex items-center justify-center py-16 text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            )}
            {!loading && rows.length === 0 && (
              <div className="text-center py-16 px-6" data-testid="advisor-list-empty">
                <div className="text-lg font-bold text-slate-400">
                  {tab === "approvals" ? "Nothing waiting on a customer" : "No cars on the board today"}
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  {tab === "approvals" ? "Every quote you've sent has an answer." : "Import the diary or add a job to get started."}
                </div>
              </div>
            )}
            {rows.map((job) => (
              <AdvisorCarRow
                key={job.id} job={job}
                selected={job.id === selectedId}
                onSelect={selectJob}
                showWait={tab === "approvals"}
                flash={!!flash[job.id]}
              />
            ))}
          </div>
        </section>

        <section className={`min-h-0 ${selected ? "block" : "hidden lg:block"} bg-slate-900/30`}>
          <AdvisorDetailPanel
            job={selected}
            onChanged={load}
            onShowCustomer={showCustomer}
            onClose={() => setSelectedId(null)}
          />
        </section>
      </main>
    </div>
  );
}
